import React, {useMemo} from 'react';
import Link from 'next/link';
import {useScopeTranslation} from '@uigstudio/uig-nextjs-helper';

import {Text} from '~components/atoms/Text';
import {PlotDetailsResponse} from '~types/Plots';
import Dome from '~assets/images/icons/plot/dome2.svg';
import ArrowRight from '~assets/images/icons/ui/arrow-right.svg';
import {Loader} from '~components/atoms/Loader';
import PlotImage from '~components/molecules/PlotPreview/PlotImage';
import {Routes} from '~constants/routes';
import {usePlotName} from '~utils/plot/address';
import {formatNumber, formatPrice} from '~utils/number';
import PlotTags from '~molecules/PlotPreview/PlotTags';
import PlotNFT from '~molecules/PlotNFT';

import {LoaderContainer} from '../PlotPreview/styles';

import {
    LandTitle,
    Price,
    PriceContainer,
    PriceText,
    Subtext,
    Wrapper,
} from './styles';

type LandInfoProps = {
    item: PlotDetailsResponse;
};

const LandPanel = ({item}: LandInfoProps) => {
    const t = useScopeTranslation('list', 'panel');
    const plotName = usePlotName(item);
    const isSold = useMemo(() => Boolean(item.isSold), [item]);
    const id = useMemo(() => Number(item.nftId.replace(':', '')), [item]);

    return (
        <Link
            href={{
                pathname: Routes.Plot,
                query: {id: item?.id},
            }}
            passHref
        >
            <Wrapper data-cy={'LandPanel'}>
                {item ? (
                    <>
                        <PlotImage plot={item} />
                        <LandTitle>{t('plot', {plot: plotName})}</LandTitle>
                        {(item.price?.startingPrice || item.price?.topBid) && (
                            <Subtext>
                                {isSold ? t('soldFor') : t('startingPrice')}
                            </Subtext>
                        )}
                        <PriceContainer>
                            {(item.price?.startingPrice || item.price?.topBid) && (
                                <Price>
                                    <Dome />
                                    <PriceText active={isSold}>
                                        {formatNumber(
                                            (
                                                item.price.topBid ||
                                                item.price.startingPrice
                                            )?.dome as number,
                                        )}
                                    </PriceText>
                                    <Subtext>
                                        {isSold &&
                                        item?.price?.topBid?.usd !== undefined
                                            ? formatPrice(
                                                  item.price.topBid.usd as number,
                                              )
                                            : t('notSold')}
                                    </Subtext>
                                </Price>
                            )}
                            <Price className={'details'}>
                                <Text>{t('seeDetails')}</Text>
                                <ArrowRight style={{marginLeft: '8px'}} />
                            </Price>
                        </PriceContainer>
                        <PlotTags plot={item} />
                        <PlotNFT
                            isLand={item.isLand}
                            owner={item.owner?.walletAddress}
                            id={id}
                        />
                    </>
                ) : (
                    <LoaderContainer>
                        <Loader />
                    </LoaderContainer>
                )}
            </Wrapper>
        </Link>
    );
};

export default LandPanel;
